/**
 * Evergreen "next airing" computation for a daily-recurring autowebinar (e.g.
 * `webinars/job-search-a-to-ai`, room runs every day at a fixed Moscow hour).
 * Pure, timezone-independent (works off UTC epoch ms + a fixed MSK offset —
 * MSK has carried no DST since 2014, so a fixed +3h offset never drifts).
 *
 * Single source of truth for the "Завтра, <weekday>, <d> <month>, HH:00 Мск"
 * text shown in the hero/final registration cards and the countdown target —
 * `useNextMskAiring` (the client hook) and `KitCountdown`'s recurring mode
 * both read this module so the three surfaces can never disagree.
 */

export const MSK_OFFSET_MS = 3 * 60 * 60 * 1000;

const MONTHS_GENITIVE = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
] as const;

// Indexed like Date#getUTCDay() (0 = Sunday).
const WEEKDAYS = [
  "воскресенье",
  "понедельник",
  "вторник",
  "среда",
  "четверг",
  "пятница",
  "суббота",
] as const;

export interface NextMskAiring {
  /** Epoch ms (UTC) of the next airing. */
  targetMs: number;
  weekday: string;
  day: number;
  month: string;
}

function pad2(value: number): string {
  return value.toString().padStart(2, "0");
}

/**
 * The next occurrence of `hourMsk:00` Moscow time, `dayOffset` calendar days
 * (MSK) ahead of `nowMs`. `dayOffset` defaults to 1 ("tomorrow") — the
 * autowebinar always advertises the next day's run, never "later today",
 * matching the live GC funnel's own daily-recurring schedule.
 */
export function nextMskAiring(nowMs: number, hourMsk: number, dayOffset = 1): NextMskAiring {
  const nowMsk = new Date(nowMs + MSK_OFFSET_MS);
  const y = nowMsk.getUTCFullYear();
  const m = nowMsk.getUTCMonth();
  const d = nowMsk.getUTCDate();
  const targetMs = Date.UTC(y, m, d + dayOffset, hourMsk, 0, 0) - MSK_OFFSET_MS;
  const targetDateMsk = new Date(Date.UTC(y, m, d + dayOffset));
  return {
    targetMs,
    weekday: WEEKDAYS[targetDateMsk.getUTCDay()],
    day: targetDateMsk.getUTCDate(),
    month: MONTHS_GENITIVE[targetDateMsk.getUTCMonth()],
  };
}

/** "Завтра, среда, 30 сентября, 19:00 Мск" — the full date/time line. */
export function formatMskAiringFull(nowMs: number, hourMsk: number, dayOffset = 1): string {
  const airing = nextMskAiring(nowMs, hourMsk, dayOffset);
  return `Завтра, ${airing.weekday}, ${airing.day} ${airing.month}, ${pad2(hourMsk)}:00 Мск`;
}

/**
 * "Завтра в 19:00 Мск" — the short form. No date math needed (it's always
 * true for a daily "tomorrow" recurrence), so this also doubles as the SSR /
 * pre-mount fallback text for the full-form surfaces.
 */
export function formatMskAiringShort(hourMsk: number): string {
  return `Завтра в ${pad2(hourMsk)}:00 Мск`;
}

export function msUntilNextMskAiring(nowMs: number, hourMsk: number, dayOffset = 1): number {
  return Math.max(0, nextMskAiring(nowMs, hourMsk, dayOffset).targetMs - nowMs);
}

const DAY_MS = 24 * 60 * 60 * 1000;

function mskDayIndex(ms: number): number {
  return Math.floor((ms + MSK_OFFSET_MS) / DAY_MS);
}

export interface UpcomingSession {
  startMs: number;
  day: number;
  month: string;
  weekday: string;
  /** "15:00" — Moscow wall clock. */
  time: string;
  /** By the Moscow calendar day; `null` further out. */
  relative: "today" | "tomorrow" | null;
}

/**
 * Registration for a session closes this long before it starts — the GC funnel
 * assigns a registrant to the first session at least this far ahead, so the
 * first card always names the session the confirmation will.
 */
export const SESSION_CLOSE_MS = 10 * 60 * 1000;

/**
 * The next `count` sessions of a fixed schedule (ISO datetimes with an offset)
 * still open at `nowMs`. A session drops out `SESSION_CLOSE_MS` before it
 * starts, so after 17:50 the list opens on tomorrow.
 */
export function upcomingSessions(
  nowMs: number,
  slots: readonly string[],
  count = 2,
): UpcomingSession[] {
  const today = mskDayIndex(nowMs);
  return slots
    .map((slot) => Date.parse(slot))
    .filter((startMs) => startMs - SESSION_CLOSE_MS > nowMs)
    .sort((a, b) => a - b)
    .slice(0, count)
    .map((startMs) => {
      const msk = new Date(startMs + MSK_OFFSET_MS);
      const ahead = mskDayIndex(startMs) - today;
      return {
        startMs,
        day: msk.getUTCDate(),
        month: MONTHS_GENITIVE[msk.getUTCMonth()],
        weekday: WEEKDAYS[msk.getUTCDay()],
        time: `${pad2(msk.getUTCHours())}:${pad2(msk.getUTCMinutes())}`,
        relative: ahead === 0 ? "today" : ahead === 1 ? "tomorrow" : null,
      };
    });
}
